import { FormikProps } from 'formik/dist/types';
import * as Yup from 'yup';
import {
  CommonData,
  ErrorWord,
  yapBoolWhen,
  yapStringWhen,
  AccountEstablishRequestParam,
  EstablishPageType,
  yapStringWithLengthWhen,
} from '../common';

/*--------------
  事前質問
  --------------*/
//勧誘業者名のあれこれ
//リスト名
enum FinancialIntermediary {
  FPGSecurities = '株式会社FPG',
}
//{ id: number; value: string }[]のデータ作る
const FinancialIntermediaryList: CommonData[] = (
  Object.keys(FinancialIntermediary) as (keyof typeof FinancialIntermediary)[]
).map((key, index) => {
  return { id: index + 1, value: FinancialIntermediary[key] };
});
//string[]のデータ作る
export const FinancialIntermediaryStrs = FinancialIntermediaryList.map((data) => data.value);
//{ id: number; value: string }[]からリスト名でid取得する
export const findFinancialIntermediaryId = (str: string) => {
  return FinancialIntermediaryList.find((data) => data.value === str)?.id;
};

//業者拠点名のあれこれ
//リスト名
enum FinancialIntermediaryCenter {
  TOKYO1 = '東京営業1部',
  TOKYO2 = '東京営業2部',
  TOKYO3 = '東京営業3部',
  WEST_TOKYO = '西東京営業部',
  SAPPORO = '札幌支店',
  MORIOKA = '盛岡営業所',
  SENDAI = '仙台支店',
  TAKASAKI = '高崎支店',
  OMIYA = '大宮支店',
  YOKOHAMA = '横浜支店',
  NAGOYA = '名古屋支店',
  KANAZAWA = '金沢支店',
  OSAKA1 = '大阪営業1部',
  OSAKA2 = '大阪営業2部',
  IMABARI = '今治営業所',
  TAKAMATSU = '高松支店',
  OKAYAMA = '岡山支店',
  HIROSHIMA = '広島支店',
  FUKUOKA = '福岡支店',
}
//{ id: number; value: string }[]のデータ作る
const FinancialIntermediaryCenterList: CommonData[] = (
  Object.keys(FinancialIntermediaryCenter) as (keyof typeof FinancialIntermediaryCenter)[]
).map((key, index) => {
  return { id: index + 1, value: FinancialIntermediaryCenter[key] };
});
//string[]のデータ作る
export const FinancialIntermediaryCenterStrs = FinancialIntermediaryCenterList.map((data) => data.value);
//{ id: number; value: string }[]からリスト名でid取得する
export const findFinancialIntermediaryCenterId = (str: string) => {
  return FinancialIntermediaryCenterList.find((data) => data.value === str)?.id;
};

//入力値の型
export type AdvanceQuestionType = {
  // 金融商品仲介業者の勧誘有無
  financialIntermediaryInvitation: boolean | null;
  // 金融商品仲介業に関する明示事項の説明有無
  financialIntermediaryExplanation: boolean | null;
  // 勧誘業者名
  financialIntermediary: string | null; //Intに変換してリクエスト必要
  // 業者拠点名
  financialIntermediaryCenter: string | null; //Intに変換してリクエスト必要
  // 担当者コード
  managerCode: string | null;
  // 年齢確認
  ageConfirmation: boolean;
  // 居住国確認
  residenceConfirmation: boolean;
  // 身分証明書確認
  idConfirmation: boolean;
  // 外国PEPs確認
  pepsConfirmation: boolean;
  // 契約締結前交付書面提供方法の同意
  electronicDocumentAgreement: boolean;
  // 契約締結前交付書面提供方法の同意の日時
  electronicDocumentAgreementDateTime: string;
  // 契約締結前交付書面提供方法の同意を読んだか。リクエストに不要
  isReadElectronicDocumentAgreement: boolean;
};
//Formik用の型
export type AdvanceQuestionFormikType = FormikProps<AdvanceQuestionType>;

//Formikに渡す初期値
export const advanceQuestionInitialValues = (param: AdvanceQuestionType, currentDateTime: string) => {
  return {
    ...param,
    financialIntermediaryInvitation: param.financialIntermediaryInvitation ?? null,
    financialIntermediaryExplanation: param.financialIntermediaryExplanation ?? null,
    financialIntermediary: param.financialIntermediary ?? null,
    financialIntermediaryCenter: param.financialIntermediaryCenter ?? null,
    managerCode: param.managerCode ?? null,
    ageConfirmation: param.ageConfirmation ?? false,
    residenceConfirmation: param.residenceConfirmation ?? false,
    idConfirmation: param.idConfirmation ?? false,
    pepsConfirmation: param.pepsConfirmation ?? false,
    electronicDocumentAgreement: param.electronicDocumentAgreement ?? false,
    electronicDocumentAgreementDateTime: currentDateTime ?? '',
    isReadElectronicDocumentAgreement: param.isReadElectronicDocumentAgreement ?? false,
  };
};

//バリデーション
export const advanceQuestionValidation = Yup.object().shape({
  financialIntermediaryInvitation: Yup.bool().required(ErrorWord.SELECT),
  financialIntermediaryExplanation: yapBoolWhen(
    'financialIntermediaryInvitation',
    true,
    ErrorWord.UABLE_OPEN_ACCOUNT_TYPE2,
  ),
  financialIntermediary: yapStringWhen('financialIntermediaryInvitation', true, ErrorWord.SELECT),
  financialIntermediaryCenter: yapStringWhen('financialIntermediaryInvitation', true, ErrorWord.SELECT),
  managerCode: yapStringWithLengthWhen('financialIntermediaryInvitation', true, 6, ErrorWord.MANAGET_CODE),
  ageConfirmation: Yup.bool().oneOf([true], ErrorWord.UABLE_OPEN_ACCOUNT_TYPE2),
  residenceConfirmation: Yup.bool().oneOf([true], ErrorWord.UABLE_OPEN_ACCOUNT_TYPE2),
  idConfirmation: Yup.bool().oneOf([true], ErrorWord.UABLE_OPEN_ACCOUNT_TYPE2),
  pepsConfirmation: Yup.bool().oneOf([true], ErrorWord.UABLE_OPEN_ACCOUNT_TYPE3),
  electronicDocumentAgreement: Yup.bool().oneOf([true], ErrorWord.UABLE_OPEN_ACCOUNT),
  isReadElectronicDocumentAgreement: Yup.bool().oneOf([true], '証券取引約款についてのリンクから内容をご確認ください'),
});

//Formikのサブミットメソッド
export const advanceQuestionOnSubmit = (
  param: AccountEstablishRequestParam,
  currentDateTime: string,
  setNowEstablishPageType: (navType: EstablishPageType) => void,
  add: (data: AccountEstablishRequestParam) => void,
) => {
  return (values: AdvanceQuestionType) => {
    const isInvitation = values.financialIntermediaryInvitation;
    add({
      ...param,
      financialIntermediaryInvitation: isInvitation,
      financialIntermediaryExplanation: isInvitation ? values.financialIntermediaryExplanation : null,
      financialIntermediary: isInvitation ? values.financialIntermediary : null,
      financialIntermediaryCenter: isInvitation ? values.financialIntermediaryCenter : null,
      managerCode: isInvitation ? values.managerCode : null,
      ageConfirmation: values.ageConfirmation,
      residenceConfirmation: values.residenceConfirmation,
      idConfirmation: values.idConfirmation,
      pepsConfirmation: values.pepsConfirmation,
      electronicDocumentAgreement: values.electronicDocumentAgreement,
      electronicDocumentAgreementDateTime: currentDateTime,
      isReadElectronicDocumentAgreement: values.isReadElectronicDocumentAgreement,
    });
    setNowEstablishPageType(EstablishPageType.ConsentSecurities);
  };
};
